import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useCart } from "../lib/cart";
import { api, errMsg, money } from "../lib/api";
import { BankDetails } from "../components/BankDetails";
import { ShippingPicker } from "../components/ShippingPicker";
import { PlanPicker, splitPlan } from "../components/PlanPicker";
import { ChristmasCheckoutBanner } from "../components/ChristmasCheckoutBanner";
import { GiftWrapOption } from "../components/GiftWrapOption";
import { useStoreInfo, useProducts } from "../lib/useProducts";

const empty = { name: "", email: "", phone: "", address: "", notes: "" };
const inputCls = "w-full rounded-2xl border-2 border-[var(--ink)] bg-white px-4 py-3 text-base outline-none focus:shadow-[4px_4px_0_var(--pink)] transition-shadow";

const Summary = ({ items, total, fee, wrap }) => (
  <div className="rounded-[28px] chunk bg-[var(--yellow)] p-6 sm:p-8" data-testid="checkout-summary">
    <h2 className="font-display font-extrabold text-2xl mb-4">Order summary</h2>
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it.key} className="flex justify-between gap-3">
          <span>{it.qty} × {it.name}{it.color ? ` (${it.color})` : ""}</span>
          <span className="font-mono-x font-bold shrink-0">{money(it.price * it.qty)}</span>
        </li>
      ))}
    </ul>
    <div className="flex justify-between border-t-2 border-[var(--ink)] mt-5 pt-4">
      <span>Shipping</span><span className="font-mono-x font-bold" data-testid="checkout-shipping-fee">{fee ? money(fee) : "Free"}</span>
    </div>
    {wrap > 0 && (
      <div className="flex justify-between mt-2" data-testid="checkout-giftwrap-row">
        <span>Gift wrapping</span><span className="font-mono-x font-bold">{money(wrap)}</span>
      </div>
    )}
    <div className="flex justify-between font-display font-extrabold text-xl mt-3">
      <span>Total</span><span className="font-mono-x" data-testid="checkout-total">{money(total + fee + wrap)}</span>
    </div>
  </div>
);

export default function Checkout() {
  const { items, total, clear } = useCart();
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [method, setMethod] = useState("courier");
  const { data: info } = useStoreInfo();
  const { data: products } = useProducts();
  const extra = items.map((i) => Number((products || []).find((p) => p.slug === i.slug)?.courier_fee ?? 0));
  const base = info?.shipping ?? {};
  const shipping = base.courier ? { ...base, courier: { ...base.courier, fee: Math.max(Number(base.courier.fee ?? 0), ...extra) } } : base;
  const fee = Number(shipping?.[method]?.fee ?? 0);
  const [giftWrap, setGiftWrap] = useState(false);
  const [giftMessage, setGiftMessage] = useState("");
  const wrapPrice = Number(info?.gift_wrap_fee ?? 0);
  const wrap = giftWrap ? wrapPrice : 0;
  const grand = total + fee + wrap;
  const [plan, setPlan] = useState(1);
  const planMin = Number(info?.plan_min_total ?? 50);
  const planOk = grand > planMin;
  const installments = planOk ? plan : 1;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { window.scrollTo(0, 0); }, [result]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/orders", { ...f, shipping_method: method, installments, gift_wrap: giftWrap, gift_message: giftWrap ? giftMessage : "", items: items.map((i) => ({ slug: i.slug, qty: i.qty, color: i.color })) });
      setResult(r.data);
      clear();
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  if (result) return <BankDetails order={result.order} bank={result.bank} shipping={result.shipping} />;

  return (
    <main className="px-4 sm:px-8 pt-28 sm:pt-36 pb-24 layer-lines min-h-screen" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto">
        <Link to="/" data-testid="checkout-back-link" className="inline-flex items-center gap-2 font-semibold hover:text-[var(--pink)] transition-colors"><ArrowLeft className="w-4 h-4" /> Keep shopping</Link>
        <h1 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 mb-10">Checkout</h1>
        {items.length === 0 ? (
          <p className="font-display text-2xl" data-testid="checkout-empty">Your cart is empty. <Link to="/" className="underline">Browse the shop</Link></p>
        ) : (
          <>
          <ChristmasCheckoutBanner method={method} />
          <div className="grid lg:grid-cols-12 gap-8">
            <form onSubmit={submit} data-testid="checkout-form" className="lg:col-span-7 rounded-[28px] chunk bg-white p-6 sm:p-8 grid sm:grid-cols-2 gap-5 order-2 lg:order-1">
              <input required placeholder="Full name *" autoComplete="name" data-testid="checkout-name-input" className={inputCls} value={f.name} onChange={set("name")} />
              <input required type="email" inputMode="email" autoComplete="email" placeholder="Email *" data-testid="checkout-email-input" className={inputCls} value={f.email} onChange={set("email")} />
              <input type="tel" inputMode="tel" autoComplete="tel" placeholder="Phone (optional)" data-testid="checkout-phone-input" className={`${inputCls} sm:col-span-2`} value={f.phone} onChange={set("phone")} />
              <div className="sm:col-span-2"><ShippingPicker options={shipping} value={method} onChange={setMethod} /></div>
              {wrapPrice > 0 && <div className="sm:col-span-2"><GiftWrapOption price={wrapPrice} checked={giftWrap} onChange={setGiftWrap} message={giftMessage} onMessage={setGiftMessage} /></div>}
              {method === "courier" && (
                <textarea required rows={3} autoComplete="street-address" placeholder="Delivery address *" data-testid="checkout-address-input" className={`${inputCls} sm:col-span-2`} value={f.address} onChange={set("address")} />
              )}
              <textarea rows={2} placeholder="Notes (optional)" data-testid="checkout-notes-input" className={`${inputCls} sm:col-span-2`} value={f.notes} onChange={set("notes")} />
              <div className="sm:col-span-2">
                {planOk ? <PlanPicker total={grand} value={installments} onChange={setPlan} />
                  : <p data-testid="plan-unavailable" className="text-sm text-[#5a5470]">Payment plans (2-4 weekly payments) are available on orders over {money(planMin)}.</p>}
              </div>
              {err && <p data-testid="checkout-error" className="sm:col-span-2 text-[#b00040] font-semibold">{err}</p>}
              <button type="submit" disabled={busy} data-testid="checkout-submit-btn"
                className="sm:col-span-2 press chunk rounded-full bg-[var(--pink)] text-white font-bold py-4 text-lg disabled:opacity-60">
                {busy ? "Placing order..." : installments > 1 ? `Place order · pay ${money(splitPlan(grand, installments)[0])} now` : `Place order · ${money(grand)}`}
              </button>
              <p className="sm:col-span-2 text-sm text-[#5a5470]">You'll pay by bank transfer. We'll show our bank details and your order reference next.</p>
            </form>
            <div className="lg:col-span-5 order-1 lg:order-2"><Summary items={items} total={total} fee={fee} wrap={wrap} /></div>
          </div>
          </>
        )}
      </div>
    </main>
  );
}
